import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as authService from '../api/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user is already logged in
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
            setUser(currentUser);
        }
        setLoading(false);
    }, []);

    const login = async (username, password) => {
        try {
            const data = await authService.login(username, password);
            setUser({
                id: data.id,
                username: data.username,
                fullName: data.fullName,
                email: data.email,
                firstLogin: data.firstLogin,
                roles: data.roles
            });

            if (data.firstLogin) {
                navigate('/first-login');
            } else {
                navigate('/dashboard');
            }

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Login failed. Please check your credentials.'
            };
        }
    };

    const changePasswordFirstLogin = async (oldPassword, newPassword) => {
        try {
            await authService.changePasswordFirstLogin(user.id, oldPassword, newPassword);

            // Update the user state
            setUser(prev => ({
                ...prev,
                firstLogin: false
            }));

            navigate('/dashboard');
            return { success: true };
        } catch (error) {
            return {
                success: false,
                message: error.message || 'Failed to change password. Please try again.'
            };
        }
    };

    const logout = () => {
        authService.logout();
        setUser(null);
        navigate('/login');
    };

    const value = {
        user,
        loading,
        login,
        logout,
        changePasswordFirstLogin,
        isAuthenticated: !!user,
        isAdmin: user && user.roles && user.roles.includes('ROLE_ADMIN'),
        isManager: user && user.roles && (user.roles.includes('ROLE_ADMIN') || user.roles.includes('ROLE_MANAGER')),
        isSalesRep: user && user.roles && user.roles.includes('ROLE_SALES_REP'),
        isDistributor: user && user.roles && user.roles.includes('ROLE_DISTRIBUTOR'),
        isFirstLogin: user && user.firstLogin
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export default AuthContext;